import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { cloneDeep } from "lodash-es";

import { intlShape, relationShape } from "../../shapes";

import {
  apiShape,
  browserShape,
  notifierShape,
  withApi,
  withBrowser,
  withNotifier,
} from "../../context";

import { withIntl } from "../../wrappers";

import {
  ActionBar,
  ActionBarButtons,
  SortActionDropDownButton,
  CreateActionButton,
} from "../ActionBar";

import SearchDocuments from "../Search/SearchDocuments";

import Heading from "../Heading";
import {
  sortBy,
  sortByNestedAttribute,
  mapDocumentsDefaults,
} from "../../utils";
import Loader from "../Loader";
import ProductDocumentItem from "./ProductDocumentItem";
import { NotFoundError } from "../../api/errors";

type OwnProps = {
  intl: intlShape;
  api: apiShape;
  browser: browserShape;
  notifier: notifierShape;
  object: {
    id?: number;
    production_date?: string;
    productId?: number;
    product_variant?: relationShape;
  };
  withCreate?: boolean;
  canCreate?: boolean;
  target?: string;
};

const defaultProps: Partial<OwnProps> = {
  withCreate: false,
  canCreate: false,
  target: undefined,
};

type Props = OwnProps & typeof defaultProps;

export function ProductDocuments({
  api,
  browser,
  notifier,
  intl,
  object,
  withCreate,
  canCreate,
  target,
}: Props) {
  const [productDocuments, setProductDocuments] = useState();
  const [initialDocuments, setInitialDocuments] = useState();
  const [fetching, setFetching] = useState(true);
  const [sortProperties, setSortProperties] = useState({
    sortOption: undefined,
    sortCounter: 0,
  });

  const loadProductDocuments = async () => {
    try {
      const filters = {
        include: "attachments, categories.parent, tenant",
        order_by: "-id",
      };
      let responseDocuments;
      if (object.product_variant) {
        responseDocuments = await api.getAll(
          `/product_variants/${object.product_variant.id}/documents`,
          filters,
        );
      }
      // no product variant or no product variant documents, load product documents
      if (!responseDocuments || !responseDocuments.documents.length) {
        responseDocuments = await api.getAll(
          `/products/${object.productId}/documents`,
          filters,
        );
      }
      const mappedDocuments = mapDocumentsDefaults(
        responseDocuments.documents,
        // @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 2.
        intl,
      );
      setProductDocuments(mappedDocuments);
      setInitialDocuments(mappedDocuments);
    } catch (error) {
      if (error instanceof NotFoundError) {
        // @ts-expect-error TS(2345) FIXME: Argument of type 'never[]' is not assignable to pa... Remove this comment to see the full error message
        setProductDocuments([]);
      } else {
        notifier.showError(api.translateError(error));
      }
    } finally {
      setFetching(false);
    }
  };

  React.useEffect(() => {
    loadProductDocuments();
  }, [object.id]);

  const sortProductDocuments = (
    option: $TSFixMe,
    counter: $TSFixMe,
    documents: $TSFixMe,
  ) => {
    const sortOrder = counter === 2 ? "ASC" : "DESC";
    const sortedDocuments = documents;
    if (sortedDocuments?.length > 1) {
      switch (option) {
        case "name":
          return sortBy(sortedDocuments, "name", sortOrder);
        case "category":
          return sortByNestedAttribute(sortedDocuments, sortOrder, [
            // @ts-expect-error TS(2322) FIXME: Type 'string' is not assignable to type 'never'.
            "defaultCategory",
            // @ts-expect-error TS(2322) FIXME: Type 'string' is not assignable to type 'never'.
            "name",
          ]);
        case "date":
          return sortByNestedAttribute(sortedDocuments, sortOrder, [
            // @ts-expect-error TS(2322) FIXME: Type 'string' is not assignable to type 'never'.
            "defaultAttachment",
            // @ts-expect-error TS(2322) FIXME: Type 'string' is not assignable to type 'never'.
            "content_date",
          ]);
        default:
          return sortBy(sortedDocuments, "id", sortOrder);
      }
    }
    return sortedDocuments;
  };

  const handleOnSearch = (documents: $TSFixMe) => {
    setProductDocuments(
      sortProductDocuments(
        sortProperties.sortOption,
        sortProperties.sortCounter,
        documents,
      ),
    );
  };

  const handleSortProductDocuments = (option: $TSFixMe, counter: $TSFixMe) => {
    if ((productDocuments as $TSFixMe)?.length > 1) {
      const sortedDocuments = sortProductDocuments(
        option,
        counter,
        productDocuments,
      );
      setProductDocuments(sortedDocuments);
      setSortProperties({ sortOption: option, sortCounter: counter });
    }
  };

  const renderProductDocumentList =
    (productDocuments as $TSFixMe)?.length > 0 ? (
      <ul id="product-document-list" className="list">
        {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
        {productDocuments.map((document: $TSFixMe) => (
          <div className="details-item" key={document.id}>
            <ProductDocumentItem
              document={document}
              withCreate={withCreate}
              canCreate={canCreate}
              productId={object.id}
            />
          </div>
        ))}
      </ul>
    ) : (
      <div id="no-product-documents-found" className="details-item">
        <FormattedMessage id="document_list.no_documents_found_hint" />
      </div>
    );

  const renderProductDocumentSearch =
    (initialDocuments as $TSFixMe)?.length > 0 ? (
      <SearchDocuments
        documentList={cloneDeep(initialDocuments)}
        onSearch={(documents: $TSFixMe) => handleOnSearch(documents)}
      />
    ) : null;

  const onProductDocumentCreate = () => {
    if (target) {
      browser.navigateTo(target);
    } else {
      browser.navigateTo("/404");
    }
  };

  const renderDocumentCreateButton = withCreate ? (
    <CreateActionButton
      // @ts-expect-error TS(2322) FIXME: Type '{ id: string; disabled: boolean; onClick: ()... Remove this comment to see the full error message
      id="create-product-document"
      disabled={!canCreate}
      onClick={() => onProductDocumentCreate()}
    />
  ) : null;

  return (
    <Loader loading={fetching}>
      <div
        id="product-documents-list"
        className="document-list product-documents-list"
      >
        <ActionBar>
          <Heading
            title={intl.formatMessage({ id: "document_list.products.header" })}
            level={2}
          />
          <ActionBarButtons>
            {renderDocumentCreateButton}
            <SortActionDropDownButton
              // @ts-expect-error TS(2322) FIXME: Type '{ id: string; handleSortClick: (option: any,... Remove this comment to see the full error message
              id="sort-dropdown-button"
              handleSortClick={(option: $TSFixMe, counter: $TSFixMe) =>
                handleSortProductDocuments(option, counter)
              }
              sortOptions={["name", "category", "date"]}
            />
          </ActionBarButtons>
        </ActionBar>
        {renderProductDocumentSearch}
        {fetching ? null : renderProductDocumentList}
      </div>
    </Loader>
  );
}

ProductDocuments.defaultProps = defaultProps;

export default withApi(withBrowser(withNotifier(withIntl(ProductDocuments))));
